import React, { useEffect, useRef, useState } from 'react';
import styles from './ImageCarousel.module.css';

interface ImageCarouselProps {
  images: string[];
  alt: string;
  className?: string;
  imageClassName?: string;
  wrapClassName?: string;
  /** Callback when carousel controls are clicked (e.g. to stop propagation for parent Link) */
  onControlClick?: (e: React.MouseEvent) => void;
}

export function ImageCarousel({ images, alt, className, imageClassName, wrapClassName, onControlClick }: ImageCarouselProps) {
  const [index, setIndex] = useState(0);
  const touchStartXRef = useRef<number | null>(null);
  const touchStartYRef = useRef<number | null>(null);
  const touchDeltaXRef = useRef(0);
  const touchDeltaYRef = useRef(0);
  const suppressClickRef = useRef(false);
  const list = images?.length ? images : [];
  const current = list[index % list.length] ?? '';
  const swipeThreshold = 40;

  useEffect(() => {
    setIndex((currentIndex) => {
      if (list.length === 0) return 0;
      return currentIndex >= list.length ? list.length - 1 : currentIndex;
    });
  }, [list.length]);

  if (list.length === 0) return null;

  const showPrev = () => {
    setIndex((i) => (i <= 0 ? list.length - 1 : i - 1));
  };

  const showNext = () => {
    setIndex((i) => (i >= list.length - 1 ? 0 : i + 1));
  };

  const goPrev = (e: React.MouseEvent) => {
    onControlClick?.(e);
    showPrev();
  };

  const goNext = (e: React.MouseEvent) => {
    onControlClick?.(e);
    showNext();
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (e.touches.length !== 1) return;

    touchStartXRef.current = e.touches[0].clientX;
    touchStartYRef.current = e.touches[0].clientY;
    touchDeltaXRef.current = 0;
    touchDeltaYRef.current = 0;
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (touchStartXRef.current === null || touchStartYRef.current === null || e.touches.length !== 1) return;

    touchDeltaXRef.current = e.touches[0].clientX - touchStartXRef.current;
    touchDeltaYRef.current = e.touches[0].clientY - touchStartYRef.current;
  };

  const resetTouchTracking = () => {
    touchStartXRef.current = null;
    touchStartYRef.current = null;
    touchDeltaXRef.current = 0;
    touchDeltaYRef.current = 0;
  };

  const handleTouchEnd = () => {
    const deltaX = touchDeltaXRef.current;
    const deltaY = touchDeltaYRef.current;
    const isHorizontalSwipe =
      Math.abs(deltaX) >= swipeThreshold && Math.abs(deltaX) > Math.abs(deltaY);

    if (isHorizontalSwipe) {
      suppressClickRef.current = true;
      if (deltaX < 0) {
        showNext();
      } else {
        showPrev();
      }
    }

    resetTouchTracking();
  };

  const handleClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!suppressClickRef.current) return;

    e.preventDefault();
    e.stopPropagation();
    suppressClickRef.current = false;
  };

  return (
    <div className={`${styles.carousel} ${className ?? ''}`}>
      <div
        className={`${styles.wrap} ${wrapClassName ?? ''}`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={resetTouchTracking}
        onClickCapture={handleClickCapture}
      >
        <img
          src={current}
          alt={list.length > 1 ? `${alt} (${index + 1} of ${list.length})` : alt}
          className={imageClassName ?? styles.image}
          draggable={false}
          loading="lazy"
          decoding="async"
        />
        {list.length > 1 && (
          <>
            <button
              type="button"
              className={styles.prev}
              onClick={goPrev}
              aria-label="Previous image"
            />
            <button
              type="button"
              className={styles.next}
              onClick={goNext}
              aria-label="Next image"
            />
            <div className={styles.dots}>
              {list.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  className={index === i ? styles.dotActive : styles.dot}
                  onClick={(e) => {
                    onControlClick?.(e);
                    setIndex(i);
                  }}
                  aria-label={`Go to image ${i + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
